<template>
  <div class="city-data-card">
    <!-- 关闭按钮 -->
    <button class="close-button" @click="handleClose">×</button>
    
    <div class="data-section">
      <h2 class="priority-title">Priority</h2>
      <p class="priority-description">Potential drivers that shape improvements in accessibility and equality to fulfill wellbeing. The variable value represents the degree of priority development, and the direction of the variable value represents the recommended inhibitory or promoting strategy.</p>
      <div class="city-header">
        <h3 class="city-name">{{ cityName }}({{ currentSelectedValue }})</h3>
        <span class="priority-value-display" :style="{ color: cityData.priorityDirection === 'positive' ? '#1976D2' : '#D32F2F' }">
          {{ getDisplayName(cityData.priorityValue) }} ({{ cityData.priorityDirection }})
        </span>
      </div>
      
  <div class="chart-container">
    <canvas ref="chartCanvas" width="480" height="250"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick, onUnmounted } from 'vue';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// 注册Chart.js组件
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
);

export default {
  name: 'PriorityChart',
  props: {
    cityData: {
      type: Object,
      required: true
    },
    cityName: {
      type: String,
      required: true
    },
    currentSelectedValue: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const chartCanvas = ref(null);
    const chartInstance = ref(null);

    // 处理关闭事件
    const handleClose = () => {
      emit('close');
    };

    // 将原始变量名转换为友好的显示名称
    const getDisplayName = (variableName) => {
      const nameMap = {
        'urban_pop': 'Urban population',
        'MESH_All': 'MESH',
        'x2_urArea': 'UBGS ratio',
        'over_thre': 'Over threshold'
      };
      return nameMap[variableName] || variableName;
    };

    // 将友好名称转换为原始变量名
    const getOriginalName = (displayName) => {
      const nameMap = {
        'Urban population': 'urban_pop',
        'MESH': 'MESH_All',
        'UBGS ratio': 'x2_urArea',
        'Over threshold': 'over_thre'
      };
      return nameMap[displayName] || displayName;
    };

    // 创建图表
    const createChart = async () => {
      // 等待下一个tick确保DOM已更新
      await nextTick();
      
      if (!chartCanvas.value) {
        console.log('Canvas not available');
        return;
      }

      // 销毁现有图表
      if (chartInstance.value) {
        chartInstance.value.destroy();
        chartInstance.value = null;
      }

      const ctx = chartCanvas.value.getContext('2d');
      if (!ctx) {
        console.log('Cannot get canvas context');
        return;
      }
      
      // 准备数据
      const factors = props.cityData?.factors || {};
      const direction = props.cityData?.direction || {};
      
      const labels = ['UBGS ratio', 'Urban population', 'Over threshold', 'MESH'];
      const data = labels.map(label => {
        const originalName = getOriginalName(label);
        return factors[originalName] || 0;
      });
      
      // 根据正负值和方向设置颜色
      const backgroundColors = labels.map(label => {
        const originalName = getOriginalName(label);
        const value = factors[originalName] || 0;
        const dir = direction[originalName] || (value >= 0 ? 'positive' : 'negative');
        
        // 使用与图例相同的颜色映射
        const colorMap = {
          'Urban population': {
            positive: '#42A5F5',
            negative: 'rgb(255, 107, 107)'
          },
          'MESH': {
            positive: '#90CAF9',
            negative: 'rgb(255, 223, 223)'
          },
          'UBGS ratio': {
            positive: '#1E88E5',
            negative: 'rgb(240, 62, 62)'
          },
          'Over threshold': {
            positive: '#64B5F6',
            negative: 'rgb(255, 182, 182)'
          }
        };
        
        return colorMap[label] ? colorMap[label][dir] : '#808080';
      });

      try {
        chartInstance.value = new ChartJS(ctx, {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [{
              label: 'Synergy Value',
              data: data,
              backgroundColor: backgroundColors,
              borderColor: backgroundColors,
              borderWidth: 1,
              barPercentage: 0.5,
              categoryPercentage: 0.8
            }]
          },
          options: {
            responsive: false,
            maintainAspectRatio: false,
            animation: false,
            layout: {
              padding: {
                top: 25,
                bottom: 15,
                left: 0,
                right: 10
              }
            },
            plugins: {
              title: {
                display: false
              },
              legend: {
                display: false
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    const label = context.label;
                    const value = context.parsed.y;
                    return `${label}: ${value.toFixed(2)}`;
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                min: (() => {
                  const maxAbsValue = Math.max(...data.map(Math.abs));
                  const minValue = Math.min(...data);
                  const baseMin = maxAbsValue > 0 ? -Math.ceil(maxAbsValue) : -1;
                  
                  // 如果有负值，确保最小刻度线与最小值的差值达到最小值的25%以上
                  if (minValue < 0) {
                    const minDifference = Math.abs(minValue) * 0.25; // 25%的差值
                    const adjustedMin = minValue - minDifference;
                    return Math.min(baseMin, Math.floor(adjustedMin));
                  }
                  
                  return baseMin - 0.5; // 没有负值时保持原有逻辑
                })(),
                max: (() => {
                  const maxAbsValue = Math.max(...data.map(Math.abs));
                  return maxAbsValue > 0 ? Math.ceil(maxAbsValue) : 1;
                })(),
                grid: {
                  color: function(context) {
                    if (context.tick.value === 0) {
                      return '#333'; // 0轴深色
                    }
                    return '#999'; // 其他网格线灰色
                  },
                  lineWidth: function(context) {
                    if (context.tick.value === 0) {
                      return 1.0; // 0轴稍粗
                    }
                    return 1;
                  },
                  borderDash: function(context) {
                    if (context.tick.value === 0) {
                      return []; // 0轴实线
                    }
                    return [3, 3]; // 其他网格线点划线
                  },
                  drawOnChartArea: true,
                  z: -1 // 设置为负值，确保网格线在柱子后面
                },
                border: {
                  display: true,
                  color: '#333',
                  width: 1.0
                },
                ticks: {
                  stepSize: (() => {
                    const maxAbsValue = Math.max(...data.map(Math.abs));
                    if (maxAbsValue <= 3) return 1;
                    if (maxAbsValue <= 6) return 2;
                    return Math.ceil(maxAbsValue / 3);
                  })(),
                  font: {
                    size: 15
                  }
                }
              },
              x: {
                grid: {
                  display: false
                },
                border: {
                  display: true,
                  color: '#333',
                  width: 1.0
                },
                ticks: {
                  font: {
                    size: 13
                  },
                  maxRotation: 0
                }
              }
            }
          },
          plugins: [{
            id: 'dataLabels',
            afterDatasetsDraw: function(chart) {
              const ctx = chart.ctx;
              chart.data.datasets.forEach((dataset, i) => {
                const meta = chart.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  
                  ctx.fillStyle = '#333';
                  ctx.font = '12px Arial';
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';
                  
                  const x = bar.x;
                  let y;
                  
                  if (data >= 0) {
                    y = bar.y - 15;
                  } else {
                    y = bar.y + 15;
                  }
                  
                  ctx.fillText(data.toFixed(3), x, y);
                });
              });
            }
          }, {
            id: 'zeroLine',
            afterDraw: function(chart) {
              const ctx = chart.ctx;
              const yScale = chart.scales.y;
              const xScale = chart.scales.x;
              
              // 获取0值在y轴上的像素位置
              const zeroY = yScale.getPixelForValue(0);
              
              // 绘制0轴线
              ctx.save();
              ctx.strokeStyle = '#333';
              ctx.lineWidth = 1.0;
              ctx.setLineDash([]);
              ctx.beginPath();
              ctx.moveTo(xScale.left, zeroY);
              ctx.lineTo(xScale.right, zeroY);
              ctx.stroke();
              ctx.restore();
            }
          }]
        });
      } catch (error) {
        console.error('Error creating chart:', error);
      }
    };

    // 监听数据变化
    watch(() => props.cityData, () => {
      if (props.cityData) {
        createChart();
      }
    }, { deep: true, immediate: false });

    // 组件挂载后创建图表
    onMounted(() => {
      if (props.cityData) {
        setTimeout(() => {
          createChart();
        }, 100);
      }
    });

    // 组件卸载时销毁图表
    onUnmounted(() => {
      if (chartInstance.value) {
        chartInstance.value.destroy();
        chartInstance.value = null;
      }
    });

    return {
      chartCanvas,
      handleClose,
      getDisplayName
    };
  }
};
</script>

<style scoped>
.city-data-card {
  width: 480px;
  height: 370px;
  padding: 12px 12px 5px 12px;
  background-color: rgba(250, 250, 250, 0.74);
  border-radius: 6px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  line-height: 1.4;
  position: absolute;
  top: 80px;
  right: 20px;
  z-index: 500; /* 降低层级,确保不会遮挡搜索下拉框 */
  overflow: hidden;
}

.data-section {
  margin-bottom: 5px;
}

/* 城市头部容器 */
.city-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 4px;
  height: auto;
  min-height: 0;
}

/* Priority标题样式 */
.priority-title {
  font-size: 18px !important;
  font-weight: 600;
  margin: 0 0 4px 0 !important;
  padding: 0 !important;
  line-height: 1.2 !important;
  color: #333;
  text-align: center;
}

/* Priority描述样式 */
.priority-description {
  font-size: 12px;
  line-height: 1.3;
  color: rgba(34, 34, 34, 0.95);
  text-align: left;
  margin: 0 0 8px 0;
  padding: 0;
  font-weight: 400;
}

/* 城市名样式 */
.city-name {
  font-size: 15px !important;
  font-weight: 600;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1.2 !important;
  color: #555;
  flex: 1;
}

/* 优先级值显示样式 */
.priority-value-display {
  font-size: 14px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 3px;
  min-width: 50px;
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;
}

/* 关闭按钮样式 - 参考IntroPanel设计 */
.close-button {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  background: rgba(92, 91, 91, 0.2);
  border: none;
  border-radius: 50%;
  font-size: 16px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  z-index: 1001;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.close-button:active {
  transform: scale(0.95);
}

.chart-container {
  width: 100%;
  height: 240px;
  margin: 0px 0; /* 减少上下边距 */
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  border-radius: 6px;
  border: none;
}

canvas {
  max-width: 100%;
  max-height: 100%;
}
</style> 